/**
 * Conversation Sidebar
 * Shows chat history organized by projects
 */

import { useEffect, useState } from 'react'
import { 
  MessageSquare, 
  Plus, 
  Search, 
  FolderPlus, 
  ChevronDown, 
  ChevronRight,
  Pin,
  Trash2,
  
  X,
  FolderOpen,
  Folder
} from 'lucide-react'
import { useConversationStore, ConversationPreview } from '@/store/conversationStore'

interface ConversationSidebarProps {
  onNewChat: () => void
  onSelectConversation: (id: string) => void
  selectedConversationId?: string
}

export default function ConversationSidebar({ 
  onNewChat, 
  onSelectConversation,
  selectedConversationId 
}: ConversationSidebarProps) {
  const {
    projects,
    conversations,
    currentProjectId,
    isLoadingConversations,
    searchQuery,
    loadProjects,
    loadConversations,
    createProject,
    deleteProject,
    createConversation,
    updateConversation,
    deleteConversation,
    setSearchQuery,
  } = useConversationStore()

  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [showNewProjectInput, setShowNewProjectInput] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [contextMenu, setContextMenu] = useState<{
    type: 'conversation' | 'project'
    id: string
    x: number
    y: number
  } | null>(null)

  useEffect(() => {
    loadProjects()
    loadConversations()
  }, [])

  const toggleProject = (projectId: string) => {
    const newExpanded = new Set(expandedProjects)
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId)
    } else {
      newExpanded.add(projectId)
    }
    setExpandedProjects(newExpanded)
  }

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return
    await createProject(newProjectName.trim())
    setNewProjectName('')
    setShowNewProjectInput(false)
  }

  const handleNewChat = async () => {
    try {
    const conv = await createConversation(currentProjectId || undefined)
      if (conv && conv.id) {
            onSelectConversation(conv.id)
            onNewChat()
      }
    } catch (error) {
      console.error("Failed to create conversation:", error)
      onNewChat() // Still clear for new chat
    }
  }

  const handleContextMenu = (
    e: React.MouseEvent,
    type: 'conversation' | 'project',
    id: string
  ) => {
    e.preventDefault()
    setContextMenu({ type, id, x: e.clientX, y: e.clientY })
  }

  const handlePinConversation = async (id: string) => {
    const conv = conversations.find(c => c.id === id)
    if (conv) {
      await updateConversation(id, { is_pinned: !conv.is_pinned })
    }
    setContextMenu(null)
  }

  const handleDeleteConversation = async (id: string) => {
    if (confirm('Delete this conversation?')) {
      await deleteConversation(id)
    }
    setContextMenu(null)
  }

  const handleDeleteProject = async (id: string) => {
    if (confirm('Delete this project? Conversations will be moved to ungrouped.')) {
      await deleteProject(id)
    }
    setContextMenu(null)
  }

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  // Group conversations by project
  const ungroupedConversations = conversations.filter(c => !c.project_id)
  const pinnedConversations = conversations.filter(c => c.is_pinned)

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    
    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    } else if (diff < 604800000) { // Less than 7 days
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  return (
    <div className="h-full flex flex-col bg-slate-900 border-r border-slate-800">
      {/* Header */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Chats</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowNewProjectInput(!showNewProjectInput)}
              className="p-2 hover:bg-slate-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              title="New Project"
            >
              <FolderPlus className="w-4 h-4" />
            </button>
            <button
              onClick={handleNewChat}
              className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors"
              title="New Chat"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* New Project Input */}
        {showNewProjectInput && (
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
              placeholder="Project name..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
            <button
              onClick={handleCreateProject}
              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm text-white"
            >
              Add
            </button>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-gray-500 hover:text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {/* Pinned Section */}
        {pinnedConversations.length > 0 && !searchQuery && (
          <div className="mb-2">
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
              <Pin className="w-3 h-3" />
              Pinned
            </div>
            {pinnedConversations.map(conv => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isSelected={selectedConversationId === conv.id}
                onClick={() =>         onSelectConversation(conv.id)}
                onContextMenu={(e) => handleContextMenu(e, 'conversation', conv.id)}
                formatTime={formatTime}
              />
            ))}
          </div>
        )}

        {/* Projects */}
        {projects.map(project => {
          const projectConversations = conversations.filter(
            c => c.project_id === project.id && !c.is_pinned
          )
          const isExpanded = expandedProjects.has(project.id)

          return (
            <div key={project.id} className="mb-1">
              <button
                onClick={() => toggleProject(project.id)}
                onContextMenu={(e) => handleContextMenu(e, 'project', project.id)}
                className="w-full px-4 py-2 flex items-center gap-2 hover:bg-slate-800 text-left"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                {isExpanded ? (
                  <FolderOpen className="w-4 h-4" style={{ color: project.color || '#a855f7' }} />
                ) : (
                  <Folder className="w-4 h-4" style={{ color: project.color || '#a855f7' }} />
                )}
                <span className="flex-1 text-sm text-gray-300 truncate">{project.name}</span>
                <span className="text-xs text-gray-600">{projectConversations.length}</span>
              </button>

              {isExpanded && projectConversations.map(conv => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isSelected={selectedConversationId === conv.id}
                  onClick={() =>         onSelectConversation(conv.id)}
                  onContextMenu={(e) => handleContextMenu(e, 'conversation', conv.id)}
                  formatTime={formatTime}
                  indent
                />
              ))}
            </div>
          )
        })}

        {/* Ungrouped */}
        {ungroupedConversations.filter(c => !c.is_pinned).length > 0 && (
          <div className="mb-2">
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">
              Recent
            </div>
            {ungroupedConversations
              .filter(c => !c.is_pinned)
              .map(conv => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isSelected={selectedConversationId === conv.id}
                  onClick={() =>         onSelectConversation(conv.id)}
                  onContextMenu={(e) => handleContextMenu(e, 'conversation', conv.id)}
                  formatTime={formatTime}
                />
              ))}
          </div>
        )}

        {/* Empty State */}
        {conversations.length === 0 && !isLoadingConversations && (
          <div className="px-4 py-8 text-center text-gray-500">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No conversations yet</p>
            <p className="text-xs mt-1">Start a new chat to begin</p>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.type === 'conversation' ? (
            <>
              <button
                onClick={() => handlePinConversation(contextMenu.id)}
                className="w-full px-4 py-2 text-sm text-left text-gray-300 hover:bg-slate-700 flex items-center gap-2"
              >
                <Pin className="w-4 h-4" />
                {conversations.find(c => c.id === contextMenu.id)?.is_pinned ? 'Unpin' : 'Pin'}
              </button>
              <button
                onClick={() => handleDeleteConversation(contextMenu.id)}
                className="w-full px-4 py-2 text-sm text-left text-red-400 hover:bg-slate-700 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          ) : (
            <button
              onClick={() => handleDeleteProject(contextMenu.id)}
              className="w-full px-4 py-2 text-sm text-left text-red-400 hover:bg-slate-700 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Project
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Conversation Item Component
function ConversationItem({
  conversation,
  isSelected,
  onClick,
  onContextMenu,
  formatTime,
  indent = false,
}: {
  conversation: ConversationPreview
  isSelected: boolean
  onClick: () => void
  onContextMenu: (e: React.MouseEvent) => void
  formatTime: (dateStr?: string) => string
  indent?: boolean
}) {
  return (
    <button
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={`w-full px-4 py-3 flex flex-col gap-1 text-left transition-colors ${
        indent ? 'pl-10' : ''
      } ${
        isSelected
          ? 'bg-purple-600/20 border-l-2 border-purple-500'
          : 'hover:bg-slate-800 border-l-2 border-transparent'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm text-white truncate font-medium">
          {conversation.title}
        </span>
        {conversation.is_pinned && (
          <Pin className="w-3 h-3 text-purple-400" />
        )}
        <span className="text-xs text-gray-500">
          {formatTime(conversation.last_message_at)}
        </span>
      </div>
      {conversation.last_message_preview && (
        <p className="text-xs text-gray-500 truncate">
          {conversation.last_message_preview}
        </p>
      )}
    </button>
  )
}
